/**
 * Marker rendering — thin glue over MapLibre.
 *
 * Uses HTML `Marker` elements (there are only ~two dozen points, so this is
 * cheap and gives full control over shape, color and accessibility). The pure
 * composition of which markers to show lives in `dayFilter.markerPointsForSelection`.
 */

import maplibregl, { type Map as MLMap, type Marker, type Popup } from 'maplibre-gl';
import type { DaySelection, Itinerary } from '../lib/types';
import { markerPointsForSelection, type MarkerPoint, boundsForSelection } from '../lib/dayFilter';
import { categoryColor, categoryLabelHe } from '../lib/colors';
import { buildCard } from '../ui/card';

const TRIP_YEAR = 2026;

// Wrap a (possibly Latin) name in Unicode isolates so it doesn't reorder the
// surrounding Hebrew in an accessible name (<bdi> can't be used in aria-label).
const FSI = '⁨';
const PDI = '⁩';
function ariaName(prefix: string, name: string): string {
  return `${prefix}: ${FSI}${name}${PDI}`;
}

function dotElement(point: MarkerPoint): HTMLElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'marker marker--dot';
  btn.style.setProperty('--marker-color', categoryColor(point.category));
  btn.setAttribute('aria-label', ariaName(categoryLabelHe(point.category), point.name));
  return btn;
}

function pinElement(point: MarkerPoint): HTMLElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'marker marker--pin';
  btn.setAttribute('aria-label', ariaName('לינה', point.name));
  const glyph = document.createElement('span');
  glyph.className = 'marker__glyph';
  glyph.setAttribute('aria-hidden', 'true');
  glyph.textContent = '★';
  btn.appendChild(glyph);
  return btn;
}

export class MarkerLayer {
  private readonly map: MLMap;
  private readonly itinerary: Itinerary;
  private markers: Marker[] = [];
  private activePopup: Popup | null = null;

  constructor(map: MLMap, itinerary: Itinerary) {
    this.map = map;
    this.itinerary = itinerary;
  }

  /** Replace all markers with those for the given selection. */
  render(selection: DaySelection): void {
    this.clear();
    const points = markerPointsForSelection(this.itinerary, selection, TRIP_YEAR);
    for (const point of points) {
      const element = point.kind === 'pin' ? pinElement(point) : dotElement(point);
      element.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openCard(point, element);
      });
      const marker = new maplibregl.Marker({ element, anchor: point.kind === 'pin' ? 'bottom' : 'center' })
        .setLngLat([point.lng, point.lat])
        .addTo(this.map);
      this.markers.push(marker);
    }
  }

  /** Fit the map to the selection's bounds (no-op if there is nothing to show). */
  fit(selection: DaySelection, options: { animate?: boolean } = {}): void {
    const b = boundsForSelection(this.itinerary, selection, TRIP_YEAR);
    if (!b) return;
    // Keep markers clear of the overlaying UI: the day-list sidebar (inline-start
    // = right, in RTL) on desktop, or the bottom-sheet peek on mobile.
    const desktop =
      typeof window !== 'undefined' && !!window.matchMedia?.('(min-width: 769px)').matches;
    const padding = desktop
      ? { top: 90, bottom: 70, left: 70, right: 360 }
      : { top: 90, bottom: 200, left: 40, right: 40 };
    this.map.fitBounds(
      [
        [b.west, b.south],
        [b.east, b.north],
      ],
      { padding, maxZoom: 14, animate: options.animate ?? true },
    );
  }

  private openCard(point: MarkerPoint, trigger: HTMLElement): void {
    this.activePopup?.remove();
    const popup = new maplibregl.Popup({
      closeButton: true,
      closeOnClick: true,
      maxWidth: '300px',
      className: 'trip-popup',
      offset: point.kind === 'pin' ? 30 : 16,
    })
      .setLngLat([point.lng, point.lat])
      .setDOMContent(
        buildCard({
          name: point.name,
          category: point.category,
          meta: point.meta,
          note: point.note,
          url: point.url,
          lat: point.lat,
          lng: point.lng,
          placeId: point.placeId,
        }),
      )
      .addTo(this.map);
    this.activePopup = popup;

    // Keyboard: move focus into the card, close on Escape, restore focus.
    popup.getElement()?.querySelector<HTMLElement>('.card')?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') popup.remove();
    };
    document.addEventListener('keydown', onKey);
    popup.once('close', () => {
      document.removeEventListener('keydown', onKey);
      if (this.activePopup === popup) this.activePopup = null;
      trigger.focus();
    });
  }

  private clear(): void {
    this.activePopup?.remove();
    this.activePopup = null;
    for (const m of this.markers) m.remove();
    this.markers = [];
  }
}
